import { useEffect, useState } from "preact/hooks";
import { useSignal } from "@preact/signals";

import { ContextMenu } from "./ContextMenu.js";
import { NodeIcon } from "./NodeIcon.js";
import type {
  ClassReference,
  FileReference,
  LoadedArtifact,
  SourceTarget,
  TreeNode,
} from "../model/types.js";

import styles from "./TreePane.module.css";

interface TreePaneProps {
  readonly artifacts: readonly LoadedArtifact[];
  readonly onOpenClass: (
    reference: ClassReference,
    target: SourceTarget,
    relativePath: string,
  ) => void;
  readonly onOpenFile: (reference: FileReference) => void;
  readonly onUnloadArtifact: (artifactId: string) => void;
  readonly onCopyRelativePath: (relativePath: string) => void;
  readonly onExportDecompiledSource: (reference: ClassReference) => void;
}

interface TreeNodeViewProps {
  readonly node: TreeNode;
  readonly depth: number;
  readonly artifactId: string;
  readonly onOpenClass: (
    reference: ClassReference,
    target: SourceTarget,
    relativePath: string,
  ) => void;
  readonly onOpenFile: (reference: FileReference) => void;
  readonly onUnloadArtifact: (artifactId: string) => void;
  readonly onShowContextMenu: (
    node: TreeNode,
    x: number,
    y: number,
  ) => void;
}

interface TreeContextMenuState {
  readonly node: TreeNode;
  readonly x: number;
  readonly y: number;
}

function errorNode(node: TreeNode, error: unknown): TreeNode {
  return {
    id: `${node.id}:load-error`,
    label: error instanceof Error ? error.message : String(error),
    kind: "error",
  };
}

function TreeNodeView({
  node,
  depth,
  artifactId,
  onOpenClass,
  onOpenFile,
  onUnloadArtifact,
  onShowContextMenu,
}: TreeNodeViewProps) {
  const expanded = useSignal(node.defaultExpanded ?? false);
  const children = useSignal<readonly TreeNode[]>(node.children ?? []);
  const loaded = useSignal(node.loadChildren === undefined);
  const loading = useSignal(false);

  const ensureLoaded = async (): Promise<void> => {
    if (loaded.value || loading.value || node.loadChildren === undefined) {
      return;
    }

    loading.value = true;
    try {
      children.value = await node.loadChildren();
    } catch (error) {
      children.value = [errorNode(node, error)];
    } finally {
      loaded.value = true;
      loading.value = false;
    }
  };

  useEffect(() => {
    if (expanded.value) {
      void ensureLoaded();
    }
  }, []);

  const canExpand =
    children.value.length > 0 || (!loaded.value && node.loadChildren !== undefined);
  const isRoot = depth === 0 && node.kind === "artifact";
  const canOpen =
    node.classReference !== undefined || node.fileReference !== undefined;

  const toggle = (): void => {
    if (!canExpand) {
      return;
    }
    expanded.value = !expanded.value;
    if (expanded.value) {
      void ensureLoaded();
    }
  };

  const select = (): void => {
    if (node.classReference !== undefined) {
      onOpenClass(
        node.classReference,
        node.sourceTarget ?? {
          kind: "class",
          internalName: node.classReference.internalName,
        },
        node.relativePath ?? node.classReference.entryPath,
      );
    } else if (node.fileReference !== undefined) {
      onOpenFile(node.fileReference);
    }
    toggle();
  };

  const rowClass = [
    styles.row,
    isRoot ? styles.rootRow : "",
    node.kind === "class" ? styles.classRow : "",
    node.kind === "error" ? styles.errorRow : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <li
      class={`${styles.item} ${isRoot ? styles.rootItem : ""}`}
      role="treeitem"
      aria-expanded={canExpand ? expanded.value : undefined}
    >
      <div
        class={rowClass}
        title={node.details ?? node.label}
        onContextMenu={(event) => {
          event.preventDefault();
          event.stopPropagation();
          onShowContextMenu(node, event.clientX, event.clientY);
        }}
      >
        <button
          type="button"
          class={styles.collapseArea}
          style={{ paddingLeft: `${8 + depth * 15}px` }}
          aria-label={
            expanded.value
              ? `Collapse ${node.label}`
              : `Expand ${node.label}`
          }
          onClick={toggle}
          disabled={!canExpand}
        >
          {loading.value ? (
            <span class={styles.miniSpinner} />
          ) : (
            <span
              class={`${styles.chevron} ${expanded.value ? styles.chevronOpen : ""}`}
            >
              ›
            </span>
          )}
        </button>
        <button
          type="button"
          class={styles.selectArea}
          onClick={select}
          aria-label={canOpen ? `Open ${node.label}` : node.label}
        >
          <NodeIcon kind={node.kind} />
          <span class={styles.nodeLabel}>{node.label}</span>
        </button>
        {isRoot ? (
          <button
            type="button"
            class={styles.unloadButton}
            aria-label={`Unload ${node.label}`}
            title={`Unload ${node.label}`}
            onClick={(event) => {
              event.stopPropagation();
              onUnloadArtifact(artifactId);
            }}
          >
            ×
          </button>
        ) : null}
      </div>
      {canExpand && expanded.value ? (
        <ul class={styles.group} role="group">
          {children.value.map((child) => (
            <TreeNodeView
              key={child.id}
              node={child}
              depth={depth + 1}
              artifactId={artifactId}
              onOpenClass={onOpenClass}
              onOpenFile={onOpenFile}
              onUnloadArtifact={onUnloadArtifact}
              onShowContextMenu={onShowContextMenu}
            />
          ))}
        </ul>
      ) : null}
    </li>
  );
}

export function TreePane({
  artifacts,
  onOpenClass,
  onOpenFile,
  onUnloadArtifact,
  onCopyRelativePath,
  onExportDecompiledSource,
}: TreePaneProps) {
  const [contextMenu, setContextMenu] = useState<TreeContextMenuState | null>(
    null,
  );

  return (
    <>
      <aside class={styles.pane} aria-label="Loaded files">
        <div class={styles.treeScroll}>
          {artifacts.length > 0 ? (
            <ul class={styles.tree} role="tree" aria-label="File structure">
              {artifacts.map((artifact) => (
                <TreeNodeView
                  key={artifact.id}
                  node={artifact.root}
                  depth={0}
                  artifactId={artifact.id}
                  onOpenClass={onOpenClass}
                  onOpenFile={onOpenFile}
                  onUnloadArtifact={onUnloadArtifact}
                  onShowContextMenu={(node, x, y) => {
                    setContextMenu({ node, x, y });
                  }}
                />
              ))}
            </ul>
          ) : null}
        </div>
      </aside>
      {contextMenu === null ? null : (
        <ContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          onClose={() => setContextMenu(null)}
          items={[
            {
              label: "Copy Relative Path",
              onSelect: () =>
                onCopyRelativePath(
                  contextMenu.node.relativePath ?? contextMenu.node.label,
                ),
            },
            {
              label: "Export Decompiled Source",
              disabled: contextMenu.node.classReference === undefined,
              onSelect: () => {
                const reference = contextMenu.node.classReference;
                if (reference !== undefined) {
                  onExportDecompiledSource(reference);
                }
              },
            },
          ]}
        />
      )}
    </>
  );
}
